import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, MoreVertical, Search, ChevronRight, ChevronLeft } from "lucide-react"
import Pagination from "react-bootstrap/Pagination";
import { useNavigate } from "react-router-dom"
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js"
import { useDispatch } from "react-redux";
import DataLoader from "../common/DataLoader.js";
import { notifyError, notifySuccess } from "../../utils/helper.js"
import EditRoleAccessModal from "../Modals/EditRoleAccessModal.js";
import { getAllUsersAllDetails, updateUserPermissions } from "../../requests.js";

const Index = () => {
    const [page, setPage] = useState(1);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState("Company");
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usersData, setUsersData] = useState([]);
    const [error, setError] = useState(null);

    const dropdownRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Load users data on component mount
    useEffect(() => {
        loadUsersData();
        loadCompanies();
    }, []);

    // Filter companies based on search term
    useEffect(() => {
        if (searchTerm) {
            const filtered = companies.filter(company =>
                company.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredCompanies(filtered);
        } else {
            setFilteredCompanies(companies);
        }
    }, [searchTerm, companies]);

    const loadUsersData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getAllUsersAllDetails();
            console.log("Response from role: ", response)
            if (response && response.data) {
                // Transform API response to match our table structure
                const transformedData = response.data.map((user, index) => ({
                    id: user.id || index + 1,
                    sl: (index + 1).toString().padStart(2, '0'),
                    name: {
                        profileImage: user.profileImage || `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'men' : 'women'}/${index + 1}.jpg`,
                        username: user.username || user.name || `User ${index + 1}`
                    },
                    email: user.email || 'No email provided',
                    role: user.role?.name || 'No role assigned',   // ✅ show role name
                    department: user.department?.name || 'No department', // ✅ show department
                    actions: "Edit",
                    originalData: user
                }));

                console.log("Transformed Data: ", transformedData)
                setUsersData(transformedData);
            } else {
                setUsersData([]);
                setError("No user data found");
            }
        } catch (error) {
            console.error("Error loading users data:", error);
            setError("Failed to load users data");
            setUsersData([]);
            notifyError("Failed to load users data");
        } finally {
            setLoading(false);
        }
    };

    const loadCompanies = async () => {
        try {
            // Mock companies data - replace with actual API call if needed
            const companiesData = ["ACME Ltd", "NextGen Corp", "Globex Corporation", "Wayne Enterprises"];
            setCompanies(companiesData);
            setFilteredCompanies(companiesData);
        } catch (error) {
            notifyError("Failed to load companies");
        }
    };

    const toggleCategoryDropdown = () => {
        setShowCategoryDropdown(!showCategoryDropdown);
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        setShowCategoryDropdown(false);
        setSearchTerm("");
        // We can add logic for filtering companywise
    };

    const handleEditAccess = async (user) => {
        console.log("User Data selecetd one: ", user)
        try {
            dispatch(startLoader());
            setSelectedUser(user);


            setUserPermissions(user.originalData?.permissions || []);

            // Mock roles and departments (or fetch from API if dynamic)
            const mockRoles = ["Admin", "Manager", "User", "Viewer"];
            const mockDepartments = ["HR", "Sales", "IT", "Finance", "Marketing"];

            setAvailableRoles(mockRoles);
            setDepartments(mockDepartments);
            setShowModal(true);

        } catch (error) {
            notifyError("Failed to load user permissions");
        } finally {
            dispatch(stopLoader());
        }
    };

    const handleSaveRoleAccess = async (permissions) => {
        console.log("Permissions: ", permissions)
        const formattedPermissions = Object.keys(permissions).map(key => {
            const perm = permissions[key];
            const levels = [];
            if (perm.view) levels.push("view_only");
            if (perm.edit) levels.push("edit");
            return { area: key, level: levels.join(",") };
        });
        try {
            dispatch(startLoader());

            const response = await updateUserPermissions(selectedUser?.id, formattedPermissions)
            console.log("Response after updating: ", response)

            notifySuccess("User permissions updated successfully");
            setShowModal(false);
            await loadUsersData();
        } catch (error) {
            notifyError("Failed to update user permissions");
        } finally {
            dispatch(stopLoader());
        }
    };


    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setUserPermissions(null);
    };

    // for pagination
    const rowsPerPage = 8;
    const totalPages = Math.ceil(usersData.length / rowsPerPage);
    const startIndex = (page - 1) * rowsPerPage;
    const paginatedData = usersData.slice(startIndex, startIndex + rowsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    };

    // Reset to page 1 when data changes
    useEffect(() => {
        setPage(1);
    }, [usersData.length]);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px'
            }}>
                <DataLoader />
            </div>
        );
    }

    if (error && usersData.length === 0) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px',
                flexDirection: 'column',
                gap: '16px'
            }}>
                <div style={{
                    color: '#6B7280',
                    fontSize: '18px',
                    fontFamily: 'Plus Jakarta Sans'
                }}>
                    {error}
                </div>
                <button
                    onClick={loadUsersData}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#23B4C3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontFamily: 'Plus Jakarta Sans'
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <div style={{
                display: "flex",
                flexDirection: "column",
                borderRadius: "21px",
                paddingBottom: "24px",
                background: " #FFFFFF",
                gap: "24px",
            }}>

                {/* header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '16px 24px',
                    borderBottom: "1px solid #D5D6DB"
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                    }}>
                        <div style={{ position: 'relative' }} ref={dropdownRef}>
                            <button
                                onClick={toggleCategoryDropdown}
                                style={{
                                    fontFamily: 'Plus Jakarta Sans',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '6px 12px',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    borderRadius: '42px',
                                    border: '1px solid #D1D5DB',
                                    background: "#FFFFFF",
                                    cursor: 'pointer',
                                    color: "#6b7280"
                                }}
                            >
                                {selectedCompany} {showCategoryDropdown ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />}
                            </button>

                            {showCategoryDropdown && (
                                <div
                                    style={{
                                        width: "200px",
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        backgroundColor: 'white',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        borderRadius: '8px',
                                        padding: '4px 0',
                                        zIndex: 999,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div style={{ position: 'relative', marginBottom: '8px', margin: "2px" }}>
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 36px 8px 12px',
                                                borderRadius: '62px',
                                                border: '1px solid #E5E7EB',
                                                fontSize: '14px',
                                                fontFamily: 'Plus Jakarta Sans',
                                                outline: 'none',
                                                background: "#f3f4f6"
                                            }}
                                        />
                                        <Search
                                            size={16}
                                            color="#6B7280"
                                            style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                            }}
                                        />
                                    </div>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {filteredCompanies.length > 0 ? (
                                            filteredCompanies.map((company, idx) => (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleCompanySelect(company)}
                                                    style={{
                                                        fontWeight: 400,
                                                        padding: '10px 16px 10px 12px',
                                                        fontSize: "14px",
                                                        fontFamily: 'Plus Jakarta Sans',
                                                        color: "#6B7280",
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        borderLeft: '4px solid transparent',
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.backgroundColor = '#E6F8F9';
                                                        e.currentTarget.style.borderLeft = '4px solid #00B3B3';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.borderLeft = '4px solid transparent';
                                                    }}
                                                >
                                                    {company}
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{
                                                padding: '10px 16px',
                                                color: '#6B7280',
                                                fontSize: '14px',
                                                textAlign: 'center'
                                            }}>
                                                No companies found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table Container */}
                <div style={{
                    margin: "0 24px",
                    overflowX: 'auto',
                    borderRadius: '12px',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {usersData.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '200px',
                            color: '#6B7280',
                            fontSize: '16px',
                            fontFamily: 'Plus Jakarta Sans'
                        }}>
                            No users found
                        </div>
                    ) : (
                        <table style={{
                            width: "100%",
                            minWidth: "1000px",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                        }}>
                            <thead>
                                <tr>
                                    <th style={{
                                        backgroundColor: '#F3F4F6',
                                        padding: '16px 24px',
                                        minWidth: '80px',
                                        textAlign: 'left',
                                        borderTopLeftRadius: "12px"
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <input
                                                type="checkbox"
                                                style={{
                                                    margin: 0,
                                                    width: '16px',
                                                    height: '16px',
                                                    accentColor: '#10B981'
                                                }}
                                            />
                                            <span style={{
                                                fontWeight: '600',
                                                color: "#111827",
                                                fontFamily: 'Plus Jakarta Sans',
                                                fontSize: '16px',
                                                lineHeight: '24px'
                                            }}>S.L</span>
                                        </div>
                                    </th>
                                    {[
                                        { label: 'Name', width: '220px' },
                                        { label: 'Email', width: '250px' },
                                        { label: 'Role', width: '180px' },
                                        { label: 'Department', width: '160px' },
                                        { label: 'Actions', width: '180px' }
                                    ].map((col, idx, array) => (
                                        <th key={idx} style={{
                                            backgroundColor: '#F3F4F6',
                                            padding: '16px 24px',
                                            minWidth: col.width,
                                            textAlign: "left",
                                            fontWeight: '600',
                                            color: "#111827",
                                            fontFamily: 'Plus Jakarta Sans',
                                            fontSize: '16px',
                                            lineHeight: '24px',
                                            ...(idx === array.length - 1 && {
                                                borderTopRightRadius: "12px"
                                            })
                                        }}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((row, index) => {
                                    const isLastRow = index === paginatedData.length - 1;
                                    return (
                                        <tr key={row.id} style={{ backgroundColor: '#fff' }}>
                                            {/* S.L */}
                                            <td style={{
                                                padding: '16px 24px',
                                                verticalAlign: 'middle',
                                                borderLeft: "1px solid #E5E7EB",
                                                borderBottom: isLastRow ? '1px solid #E5E7EB' : '1px solid #E5E7EB',
                                                borderBottomLeftRadius: isLastRow ? "12px" : "0"
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <input
                                                        type="checkbox"
                                                        style={{
                                                            margin: 0,
                                                            width: '16px',
                                                            height: '16px',
                                                            accentColor: '#10B981'
                                                        }}
                                                    />
                                                    <span style={{
                                                        color: '#6B7280',
                                                        fontWeight: '500',
                                                        fontSize: '16px',
                                                        lineHeight: '24px',
                                                        fontFamily: 'Plus Jakarta Sans'
                                                    }}>
                                                        {row.sl}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Name with profile image */}
                                            <td style={{
                                                padding: '16px 24px',
                                                verticalAlign: 'middle',
                                                borderBottom: isLastRow ? '1px solid #E5E7EB' : '1px solid #E5E7EB'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <img
                                                        src={row.name.profileImage}
                                                        alt={row.name.username}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '8px',
                                                            objectFit: 'cover'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(row.name.username)}&background=random`;
                                                        }}
                                                    />
                                                    <span style={{
                                                        color: '#6B7280',
                                                        fontWeight: '500',
                                                        fontSize: '16px',
                                                        lineHeight: '24px',
                                                        fontFamily: 'Plus Jakarta Sans'
                                                    }}>
                                                        {row.name.username}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Email */}
                                            <td style={{
                                                padding: '16px 24px',
                                                verticalAlign: 'middle',
                                                borderBottom: isLastRow ? '1px solid #E5E7EB' : '1px solid #E5E7EB'
                                            }}>
                                                <span style={{
                                                    color: '#6B7280',
                                                    fontWeight: '500',
                                                    fontSize: '15px',
                                                    fontFamily: 'Plus Jakarta Sans'
                                                }}>
                                                    {row.email}
                                                </span>
                                            </td>

                                            {/* Role */}
                                            <td style={{
                                                padding: '16px 24px',
                                                verticalAlign: 'middle',
                                                borderBottom: isLastRow ? '1px solid #E5E7EB' : '1px solid #E5E7EB'
                                            }}>
                                                <span style={{
                                                    color: '#6B7280',
                                                    fontWeight: '500',
                                                    fontSize: '15px',
                                                    fontFamily: 'Plus Jakarta Sans'
                                                }}>
                                                    {row.role}
                                                </span>
                                            </td>

                                            {/* Department */}
                                            <td style={{
                                                padding: '16px 24px',
                                                verticalAlign: 'middle',
                                                borderBottom: isLastRow ? '1px solid #E5E7EB' : '1px solid #E5E7EB'
                                            }}>
                                                <span style={{
                                                    color: '#6B7280',
                                                    fontWeight: '500',
                                                    fontSize: '15px',
                                                    fontFamily: 'Plus Jakarta Sans'
                                                }}>
                                                    {row.department}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td style={{
                                                padding: '16px 24px',
                                                textAlign: 'left',
                                                verticalAlign: 'middle',
                                                borderRight: "1px solid #E5E7EB",
                                                borderBottom: isLastRow ? '1px solid #E5E7EB' : '1px solid #E5E7EB',
                                                borderBottomRightRadius: isLastRow ? "12px" : "0"
                                            }}>
                                                <button
                                                    onClick={() => handleEditAccess(row)}
                                                    style={{
                                                        borderRadius: "27px",
                                                        padding: "8px 20px",
                                                        border: "1px solid #9CA3AF",
                                                        color: "#6B7280",
                                                        fontFamily: "Plus Jakarta Sans",
                                                        fontWeight: '500',
                                                        lineHeight: '20px',
                                                        fontSize: "14px",
                                                        verticalAlign: "middle",
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    Edit Access
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {usersData.length > 0 && totalPages > 1 && (
                    <div style={{ padding: '0 24px' }}>
                        <div className="d-flex justify-content-start align-items-center gap-3">
                            {/* Previous Button */}
                            <button
                                disabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: page === 1 ? '#9CA3AF' : '#23B4C3',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    fontFamily: 'Plus Jakarta Sans',
                                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => {
                                    if (page !== 1) {
                                        e.currentTarget.style.backgroundColor = '#F0F9FF';
                                    }
                                }}
                                onMouseLeave={e => {
                                    if (page !== 1) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                <ChevronLeft size={16} />
                                Previous
                            </button>

                            {/* Page Numbers */}
                            <div className="d-flex gap-2">
                                {[...Array(totalPages)].map((_, idx) => {
                                    const pageNum = idx + 1;
                                    const isActive = pageNum === page;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '8px',
                                                border: isActive ? '1px solid #23B4C3' : "none",
                                                backgroundColor: isActive ? '#E0F7FA' : '#FFFFFF',
                                                color: isActive ? '#23B4C3' : '#6B7280',
                                                fontSize: '14px',
                                                fontWeight: isActive ? 600 : 500,
                                                fontFamily: 'Plus Jakarta Sans',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={e => {
                                                if (!isActive) {
                                                    e.currentTarget.style.borderColor = '#23B4C3';
                                                    e.currentTarget.style.backgroundColor = '#F0F9FF';
                                                    e.currentTarget.style.color = '#23B4C3';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (!isActive) {
                                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                                                    e.currentTarget.style.color = '#6B7280';
                                                }
                                            }}
                                        >
                                            {pageNum.toString().padStart(2, '0')}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Next Button */}
                            <button
                                disabled={page === totalPages}
                                onClick={() => handlePageChange(page + 1)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '8px 12px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: page === totalPages ? '#9CA3AF' : '#23B4C3',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    fontFamily: 'Plus Jakarta Sans',
                                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={e => {
                                    if (page !== totalPages) {
                                        e.currentTarget.style.backgroundColor = '#F0F9FF';
                                    }
                                }}
                                onMouseLeave={e => {
                                    if (page !== totalPages) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Role Access Modal */}
            <EditRoleAccessModal
                show={showModal}
                onHide={handleCloseModal}
                onSave={handleSaveRoleAccess}
                user={selectedUser}
                userPermissions={userPermissions}
                availableRoles={availableRoles}
                departments={departments}
            />
        </div>
    )
}

export default Index;